import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { X, Trash2, Copy, UserPlus, Share2 } from 'lucide-react'
import { tabAPI } from '../services/api'
import toast from 'react-hot-toast'
import { useDashboardStore } from '../stores/dashboardStore'
import { useModalClose } from '../hooks/useModalClose'
import axios from 'axios'

interface TabSettingsModalProps {
  tab: any
  onClose: () => void
  onSave: () => void
  onDelete?: () => void
  onDuplicate?: () => void
}

const ICON_OPTIONS = [
  { value: 'home', label: '🏠 Home', icon: '🏠' },
  { value: 'briefcase', label: '💼 Work', icon: '💼' },
  { value: 'users', label: '👥 Family', icon: '👥' },
  { value: 'star', label: '⭐ Favorites', icon: '⭐' },
  { value: 'calendar', label: '📅 Calendar', icon: '📅' },
  { value: 'book', label: '📚 Reading', icon: '📚' },
  { value: 'music', label: '🎵 Music', icon: '🎵' },
  { value: 'game', label: '🎮 Gaming', icon: '🎮' },
  { value: 'shopping', label: '🛒 Shopping', icon: '🛒' },
  { value: 'plane', label: '✈️ Travel', icon: '✈️' },
  { value: 'heart', label: '❤️ Health', icon: '❤️' },
  { value: 'code', label: '💻 Development', icon: '💻' },
]

export default function TabSettingsModal({ tab, onClose, onSave, onDelete, onDuplicate }: TabSettingsModalProps) {
  const { tabs } = useDashboardStore()
  const queryClient = useQueryClient()
  const { handleBackdropClick } = useModalClose(onClose)
  const [activeSection, setActiveSection] = useState<'settings' | 'sharing'>('settings')
  const [settings, setSettings] = useState({
    name: tab.name || '',
    icon: tab.icon || 'home',
  })
  const [email, setEmail] = useState('')
  const [permissionType, setPermissionType] = useState<'view' | 'edit'>('view')

  const canDelete = Array.isArray(tabs) && tabs.length > 1

  // Fetch tab permissions
  const { data: permissions = [] } = useQuery({
    queryKey: ['tab-permissions', tab.id],
    queryFn: async () => {
      const token = localStorage.getItem('token')
      const { data } = await axios.get(
        `http://localhost:8000/api/tabs/${tab.id}/permissions`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )
      return data
    },
    enabled: activeSection === 'sharing'
  })

  const handleSave = async () => {
    if (!tab.id) {
      toast.error('Tab ID is missing')
      return
    }
    
    try {
      await tabAPI.update(tab.id, settings)
      toast.success('Tab settings saved!')
      onSave()
      onClose()
    } catch (error) {
      toast.error('Failed to save settings')
    }
  }

  const handleDuplicate = async () => {
    try {
      await tabAPI.duplicate(tab.id)
      toast.success('Tab duplicated!')
      onDuplicate?.()
      onClose()
    } catch (error) {
      toast.error('Failed to duplicate tab')
    }
  }

  const handleDelete = async () => {
    if (!canDelete) {
      toast.error('Cannot delete the last tab')
      return
    }

    if (!confirm(`Are you sure you want to delete the tab "${tab.name}"? All widgets on this tab will be deleted.`)) {
      return
    }

    try {
      await tabAPI.delete(tab.id)
      toast.success('Tab deleted!')
      onDelete?.()
      onClose()
    } catch (error) {
      toast.error('Failed to delete tab')
    }
  }

  const handleInvite = async () => {
    if (!email) {
      toast.error('Please enter an email address')
      return
    }

    try {
      const token = localStorage.getItem('token')
      await axios.post(
        `http://localhost:8000/api/tabs/${tab.id}/permissions`,
        { email, permission_type: permissionType },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      
      queryClient.invalidateQueries({ queryKey: ['tab-permissions', tab.id] })
      toast.success(`Invited ${email} with ${permissionType} access`)
      setEmail('')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to send invitation')
    }
  }

  const handleRevoke = async (permissionId: number) => {
    if (!confirm('Are you sure you want to revoke this permission?')) return

    try {
      const token = localStorage.getItem('token')
      await axios.delete(
        `http://localhost:8000/api/permissions/${permissionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      
      queryClient.invalidateQueries({ queryKey: ['tab-permissions', tab.id] })
      toast.success('Permission revoked')
    } catch (error) {
      toast.error('Failed to revoke permission')
    }
  }

  const getPermissionBadgeClass = (type: string) => {
    switch (type) {
      case 'edit':
        return 'bg-primary-100 text-blue-800'
      case 'view':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[3000] flex items-center justify-center p-4" onClick={handleBackdropClick}>
      <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Tab: {tab.name}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition">
            <X size={24} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveSection('settings')}
            className={`px-4 py-2 font-medium transition relative ${
              activeSection === 'settings'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Settings
          </button>
          <button
            onClick={() => setActiveSection('sharing')}
            className={`px-4 py-2 font-medium transition relative flex items-center gap-2 ${
              activeSection === 'sharing'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Share2 size={16} />
            Sharing
            {permissions.length > 0 && (
              <span className="px-1.5 py-0.5 text-xs bg-primary-100 text-primary-700 rounded-full">
                {permissions.length}
              </span>
            )}
          </button>
        </div>

        {activeSection === 'settings' && (
          <div className="space-y-6">
          {/* Tab Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tab Name
            </label>
            <input
              type="text"
              value={settings.name}
              onChange={(e) => setSettings({ ...settings, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Tab name"
            />
          </div>

          {/* Icon Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tab Icon
            </label>
            <div className="grid grid-cols-4 gap-2">
              {ICON_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSettings({ ...settings, icon: option.value })}
                  className={`p-3 border rounded-lg text-center transition ${
                    settings.icon === option.value
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-2xl mb-1">{option.icon}</div>
                  <div className="text-xs text-gray-600">{option.label.split(' ')[1]}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Info Note */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Page colors and fonts are now controlled by your active theme. 
              Use the Theme Selector (🎨 icon in header) to change the overall appearance.
            </p>
          </div>
          </div>
        )}

        {activeSection === 'sharing' && (
          <div className="space-y-6">
            {/* Invite Section */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Invite People to This Tab</h3>
              
              <div className="flex gap-2 mb-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
                />
                <select
                  value={permissionType}
                  onChange={(e) => setPermissionType(e.target.value as any)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="view">View Only</option>
                  <option value="edit">Can Edit</option>
                </select>
                <button
                  onClick={handleInvite}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition flex items-center gap-2"
                >
                  <UserPlus size={20} />
                  Invite
                </button>
              </div>

              <div className="text-xs text-gray-600">
                <p className="mb-1"><strong>View Only:</strong> Can view widgets on this tab</p>
                <p><strong>Can Edit:</strong> Can add, edit, and remove widgets on this tab</p>
              </div>
            </div>

            {/* Current Permissions */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                People with Access ({permissions.length})
              </h3>

              {permissions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No one else has access to this tab yet.</p>
                  <p className="text-sm mt-1">Invite people using the form above.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {permissions.map((permission: any) => (
                    <div
                      key={permission.id}
                      className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                    >
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{permission.username}</div>
                        <div className="text-sm text-gray-600">{permission.email}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          Granted {new Date(permission.granted_at).toLocaleDateString()}
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${getPermissionBadgeClass(permission.permission_type)}`}>
                          {permission.permission_type === 'view' ? 'View Only' : 'Can Edit'}
                        </span>
                        <button
                          onClick={() => handleRevoke(permission.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="Revoke access"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeSection === 'settings' ? (
          <div className="flex gap-3 mt-8">
            <button
              onClick={handleSave}
              className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-semibold"
            >
              Save Settings
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleDuplicate}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition flex items-center gap-2"
              title="Duplicate Tab"
            >
              <Copy size={18} />
              Duplicate
            </button>
            {canDelete && (
              <button
                onClick={handleDelete}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-2"
                title="Delete Tab"
              >
                <Trash2 size={18} />
                Delete
              </button>
            )}
          </div>
        ) : (
          <div className="flex gap-3 mt-8">
            <button
              onClick={onClose}
              className="w-full px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

